/**
 * `NotFoundScreen` — GVW-163 standalone state page.
 *
 * 404 page not found state. Live region role='status' per error-catalog.md.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-163/brief.md
 * Copy: error-catalog.md GV-ERR-404, copy-deck.md gvw.163.*
 */

import { useEffect } from 'react';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export function NotFoundScreen() {
  const liveRegion = useLiveRegion();

  useEffect(() => {
    // Copy key: gvw.163.title
    document.title = 'Page not found · GeoViewer';
    // Status role per error-catalog.md GV-ERR-404
    liveRegion.announce('status', 'Page not found');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="not-found-heading"
      data-gvw-id="GVW-163"
      data-route="/404"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[6]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.space[4],
          textAlign: 'center',
        }}
      >
        {/* Hero glyph per brief.md icon plan */}
        <div
          aria-hidden="true"
          style={{
            fontSize: 32,
            color: tokens.color.ink.secondary,
            fontFamily: 'Material Symbols Outlined',
          }}
        >
          error
        </div>

        {/* Copy key: gvw.163.title from copy-deck.md */}
        <h1
          id="not-found-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          Page not found
        </h1>

        {/* Copy key: gvw.163.body */}
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            maxWidth: '42ch',
          }}
        >
          The link you followed may be broken, or the page may have moved.
        </p>

        {/* Recovery action per navigation-map.md §8 */}
        <button
          type="button"
          onClick={() => {
            // Go to workspace per brief.md §10
            window.location.href = '/workspace';
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            fontFamily: 'inherit',
            cursor: 'pointer',
            textDecoration: 'none',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'Material Symbols Outlined',
              fontSize: 18,
            }}
          >
            home
          </span>
          Go to your workspace
        </button>
      </section>
    </main>
  );
}